import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Zap, Trash2, Link2, Plus, X, Target, Award, Ban, RotateCcw, Edit3,
} from "lucide-react";
import { api } from "../lib/api";
import Tip from "../components/Tip";
import MetaAchieveModal from "../components/MetaAchieveModal";
import MetaCancelModal from "../components/MetaCancelModal";
import AzioneEditModal from "../components/AzioneEditModal";
import { celebrate } from "../lib/confetti";

const LEVEL_LABEL = { annual: "Anno", monthly: "Mese", weekly: "Settimana" };
const LEVEL_COLOR = { annual: "#C3DAF4", monthly: "#F5C24A", weekly: "#8FBF8F" };

const HORIZON_LABEL = { "1_year": "1 anno", "5_years": "5 anni", "10_years": "10 anni" };

export default function AzioneDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [goals, setGoals] = useState([]);
  const [showLink, setShowLink] = useState(false);
  const [showAchieve, setShowAchieve] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.getMeta(id).then(setMeta).catch(() => toast.error("Errore caricamento"));
  useEffect(() => {
    load();
    api.listGoals({ status: "active" }).then(setGoals).catch(() => {});
  }, [id]); // eslint-disable-line

  if (!meta) return <div className="opacity-60 text-sm py-10 text-center" data-testid="azione-detail-loading">Caricamento…</div>;

  const pct = Number(meta.cached_progress || 0);
  const barColor = pct >= 100 ? "#3F8F5C" : pct >= 60 ? "#8FBF8F" : pct >= 30 ? "#F5C24A" : "#95BAEF";
  const accent = LEVEL_COLOR[meta.level];

  const markComplete = async () => {
    setBusy(true);
    try {
      // Backward-compat quick toggle: apre modale in caso di achieve
      setShowAchieve(true);
    } finally { setBusy(false); }
  };
  const doRestore = async () => {
    try { await api.restoreMeta(id); toast.success("Azione riportata attiva"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const del = async () => {
    if (!window.confirm("Eliminare definitivamente questa azione? Non è recuperabile.")) return;
    try { await api.deleteMeta(id); toast.success("Eliminata"); nav("/azioni"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const linkGoal = async (goalId) => {
    try {
      await api.updateMeta(id, { meta_id: goalId || null });
      toast.success(goalId ? "Collegata alla meta" : "Scollegata dalla meta");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const unlink = async (taskId) => {
    try { await api.unlinkTaskFromMeta(id, taskId); toast.success("Ciclo scollegato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-5" data-testid="azione-detail-page">
      <div className="pxos-card p-5" style={{ borderLeft: `4px solid ${accent}` }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="pxos-badge text-[10px] uppercase tracking-widest" style={{ background: accent }}>
              {LEVEL_LABEL[meta.level]}
            </div>
            <h1 className="mt-2 font-display text-2xl sm:text-3xl font-semibold inline-flex items-center gap-2">
              <Zap size={22}/> {meta.title}
            </h1>
            {meta.description && (
              <p className="mt-2 text-sm opacity-80 whitespace-pre-wrap">{meta.description}</p>
            )}
            {meta.deadline_date && (
              <div className="mt-2 text-xs opacity-70">
                📅 Scadenza: <b>{new Date(meta.deadline_date + "T00:00:00").toLocaleDateString("it-IT")}</b>
              </div>
            )}
            {meta.status === "achieved" && (
              <div className="mt-2 pxos-badge inline-flex items-center gap-1" style={{ background: "#DCFCE7", color: "#166534" }}>
                <Award size={11}/> Raggiunta{meta.achieved_at && ` · ${new Date(meta.achieved_at).toLocaleDateString("it-IT")}`}
              </div>
            )}
            {meta.status === "cancelled" && (
              <div className="mt-2 pxos-badge inline-flex items-center gap-1" style={{ background: "#F3F4F6", color: "#4B5563" }}>
                <Ban size={11}/> Annullata{meta.cancelled_at && ` · ${new Date(meta.cancelled_at).toLocaleDateString("it-IT")}`}
              </div>
            )}
            {meta.cancelled_reason && (
              <div className="mt-1 text-xs opacity-70 italic" data-testid="azione-cancel-reason">
                Motivo: {meta.cancelled_reason}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 items-end">
            {meta.status === "active" && (
              <>
                <button onClick={() => setShowAchieve(true)} disabled={busy}
                        className="rounded-full px-3 py-1.5 text-xs inline-flex items-center gap-1"
                        style={{ background: "#DCFCE7", color: "#166534" }}
                        data-testid="azione-achieve">
                  <Award size={12}/> Raggiunta
                </button>
                <button onClick={() => setShowCancel(true)}
                        className="rounded-full border px-3 py-1.5 text-xs inline-flex items-center gap-1 opacity-80"
                        data-testid="azione-cancel">
                  <Ban size={12}/> Annulla
                </button>
              </>
            )}
            {meta.status !== "active" && (
              <button onClick={doRestore}
                      className="rounded-full border px-3 py-1.5 text-xs inline-flex items-center gap-1"
                      data-testid="azione-restore">
                <RotateCcw size={12}/> Ripristina
              </button>
            )}
            <div className="flex gap-1">
              <Tip label="Modifica azione">
                <button onClick={() => setShowEdit(true)}
                        className="rounded-full border px-2.5 py-1.5 text-xs inline-flex items-center gap-1"
                        data-testid="azione-edit">
                  <Edit3 size={12}/> Modifica
                </button>
              </Tip>
              <Tip label="Elimina definitivamente">
                <button onClick={del} className="rounded-full border px-2.5 py-1.5 text-xs text-red-700" data-testid="azione-delete">
                  <Trash2 size={12}/>
                </button>
              </Tip>
            </div>
          </div>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between text-xs opacity-70 mb-1">
            <span>Progresso complessivo</span>
            <span className="font-mono font-semibold" data-testid="azione-detail-progress">{pct.toFixed(1)}%</span>
          </div>
          <div className="h-3 rounded-full overflow-hidden bg-black/5">
            <div className="h-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: barColor }}/>
          </div>
        </div>

        {/* Contribuisce alla Meta */}
        <div className="mt-5 pt-4 border-t" style={{ borderColor: "var(--pxos-border)" }} data-testid="azione-contribuisce-block">
          <div className="text-xs uppercase tracking-widest opacity-70 flex items-center gap-1 mb-2">
            <Target size={13}/> Contribuisce alla meta
          </div>
          {goals.length === 0 ? (
            <div className="text-xs opacity-60">
              Nessuna meta a 1/5/10 anni definita. {" "}
              <Link to="/mete" className="underline">Crea una meta</Link> per collegare questa azione.
            </div>
          ) : (
            <select className="pxos-input"
                    value={meta.meta_id || ""}
                    onChange={(e) => linkGoal(e.target.value || null)}
                    data-testid="azione-meta-select">
              <option value="">— nessuna —</option>
              {goals.map((g) => (
                <option key={g.id} value={g.id}>
                  [{HORIZON_LABEL[g.horizon]}] {g.title}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Linked tasks */}
      <div className="pxos-card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-lg font-semibold inline-flex items-center gap-2">
            <Link2 size={16}/> Cicli collegati
            <span className="text-[10px] uppercase tracking-widest opacity-60 px-1.5 py-0.5 rounded-full border">
              {(meta.linked_tasks || []).length}
            </span>
          </div>
          <button onClick={() => setShowLink(true)} className="pxos-btn-primary text-xs" data-testid="azione-link-open">
            <Plus size={12}/> Collega ciclo
          </button>
        </div>
        {(meta.linked_tasks || []).length === 0 ? (
          <div className="text-sm opacity-60 py-6 text-center" data-testid="azione-linked-empty">
            Nessun ciclo collegato. Collega uno o più cicli per iniziare a tracciare il progresso.
          </div>
        ) : (
          <ul className="divide-y" style={{ borderColor: "var(--pxos-border)" }}>
            {meta.linked_tasks.map((t) => (
              <li key={t.id} className="flex items-center gap-3 py-2" data-testid="azione-linked-task-row">
                <span className={`h-6 w-6 rounded-full grid place-items-center text-[10px] font-bold ${t.status === "completed" ? "bg-green-100 text-green-700" : "bg-black/5"}`}>
                  {t.status === "completed" ? "✓" : "○"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm truncate ${t.status === "completed" ? "line-through opacity-60" : "font-medium"}`}>{t.title}</div>
                  <div className="text-[11px] opacity-60">
                    {t.due_date && <>Scadenza {new Date(t.due_date + "T00:00:00").toLocaleDateString("it-IT")} · </>}
                    Peso: {t.weight.toFixed(1)}
                  </div>
                </div>
                <button onClick={() => unlink(t.id)}
                        className="text-xs opacity-60 hover:opacity-100 hover:text-red-700"
                        title="Scollega"
                        data-testid={`azione-unlink-${t.id}`}>
                  <X size={14}/>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showLink && <LinkTaskModal metaId={id} onClose={() => setShowLink(false)} onLinked={() => { setShowLink(false); load(); }}/>}
      {showAchieve && (
        <MetaAchieveModal
          entity={{ title: meta.title }}
          showNotes={false}
          onClose={() => setShowAchieve(false)}
          onConfirm={async ({ achieved_date }) => {
            await api.achieveMeta(id, { achieved_date });
            setShowAchieve(false); load(); celebrate({ durationMs: 1600 });
            toast.success("🎯 Azione raggiunta");
          }}
        />
      )}
      {showCancel && (
        <MetaCancelModal
          entity={{ title: meta.title }}
          onClose={() => setShowCancel(false)}
          onConfirm={async ({ reason }) => {
            await api.cancelMeta(id, reason);
            setShowCancel(false); load();
            toast.success("Azione annullata");
          }}
        />
      )}
      {showEdit && (
        <AzioneEditModal
          azione={meta}
          goals={goals}
          onClose={() => setShowEdit(false)}
          saveFn={(patch) => api.updateMeta(id, patch)}
          onSaved={(m) => { setMeta(m); setShowEdit(false); }}
        />
      )}
    </div>
  );
}

function LinkTaskModal({ metaId, onClose, onLinked }) {
  const [tasks, setTasks] = useState([]);
  const [query, setQuery] = useState("");
  const [weight, setWeight] = useState(1.0);
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.listTasks({ status: "open" }).then((rows) => setTasks(rows || [])).catch(() => {});
  }, []);
  const filtered = tasks.filter((t) => !query || t.title.toLowerCase().includes(query.toLowerCase()));

  const submit = async () => {
    if (!selectedId) { toast.error("Seleziona un ciclo"); return; }
    setBusy(true);
    try {
      await api.linkTaskToMeta(metaId, selectedId, weight);
      toast.success("Ciclo collegato");
      onLinked();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="azione-link-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold">Collega un ciclo</h3>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <input className="pxos-input" placeholder="Cerca ciclo…" value={query}
               onChange={(e) => setQuery(e.target.value)} data-testid="azione-link-search"/>
        <div className="border rounded-lg max-h-64 overflow-y-auto" style={{ borderColor: "var(--pxos-border)" }}>
          {filtered.length === 0 ? (
            <div className="p-3 text-sm opacity-60 text-center">Nessun ciclo trovato</div>
          ) : (
            filtered.map((t) => (
              <label key={t.id} className={`flex items-center gap-3 p-2 cursor-pointer hover:bg-black/[0.03] ${selectedId === t.id ? "bg-blue-50" : ""}`}>
                <input type="radio" name="task-select" checked={selectedId === t.id}
                       onChange={() => setSelectedId(t.id)} data-testid={`azione-link-task-${t.id}`}/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{t.title}</div>
                  <div className="text-[11px] opacity-60">
                    {t.type} · {t.priority}
                    {t.due_date && <> · scadenza {new Date(t.due_date + "T00:00:00").toLocaleDateString("it-IT")}</>}
                  </div>
                </div>
              </label>
            ))
          )}
        </div>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Peso (0.1 – 10.0)</div>
          <input type="number" step="0.1" min={0.1} max={10} value={weight}
                 onChange={(e) => setWeight(parseFloat(e.target.value) || 1.0)}
                 className="pxos-input" data-testid="azione-link-weight"/>
          <div className="text-[10px] opacity-60 mt-1">
            Più alto = questo ciclo pesa di più nel calcolo del progresso.
          </div>
        </label>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="text-sm underline opacity-70">Annulla</button>
          <button disabled={busy || !selectedId} onClick={submit}
                  className="pxos-btn-primary text-sm" data-testid="azione-link-submit">
            <Link2 size={12}/> Collega
          </button>
        </div>
      </div>
    </div>
  );
}
